/** @jsxImportSource preact */
import { useEffect, useRef, useState } from 'preact/hooks'
import { callApi } from '../api'
import { config } from '../config'
import { getFraudSettings } from './fraud-settings-api'

/**
 * The prior-default panel on the Fraud screen.
 *
 * Shows every defaulted booking behind this customer, not just the one the auto-reject named:
 * clearing one reference off a customer with three leaves the other two to block the next
 * booking, and the review note would not explain why.
 *
 * A waiver never approves a booking — it downgrades the automatic cancel-and-refund to a manual
 * fraud check, which is the decision this same screen then records.
 */

interface Waiver {
  id: number
  bookingReference: string
  reasonCode: string
  reasonNote: string | null
  pastDueAtWaiver: string
  requireZeroBalance: boolean
  waivedBy: string
  approvedBy: string | null
  created: string
}

interface PriorDefault {
  /** null when bmAPI could not return the payment record — unknown, not zero. */
  pastDue: number | null
  /**
   * False when the figure comes from a Stripe plan (which can read zero while money is owed) or
   * could not be read. The number is then not shown as fact and does not excuse the second name.
   */
  balanceVerified: boolean
  reference: string
  /** How the fraud check tied this booking to the one under review. */
  linkedBy: Array<'name' | 'email'>
  waiver: Waiver | null
}

/**
 * Mirrors the API exactly: it demands a second name when `pastDue > approvalThreshold`. Comparing
 * against a hardcoded zero here would make a stack that raised the threshold have no effect on
 * the screen. An unknown balance is not evidence of payment, so it counts as owing.
 */
const needsSecondName = (priorDefault: PriorDefault, threshold: number) =>
  !priorDefault.balanceVerified || priorDefault.pastDue === null || priorDefault.pastDue > threshold

/**
 * Two different rules, deliberately not sharing a number. `require_zero_balance` holds a waiver
 * dormant until the balance is actually zero; the approval threshold decides whether a second
 * name is needed. A stack can move the threshold, and that must not quietly move dormancy too.
 */
const isSettled = (priorDefault: PriorDefault) => priorDefault.balanceVerified && priorDefault.pastDue === 0

/** Selectable reasons, in the order they appear in the dropdown. */
const REASON_CODES = ['balance_cleared', 'hardship_plan_agreed', 'default_disputed_resolved', 'system_error']

/**
 * Labels cover more than the dropdown offers: a waiver carried over from the old spreadsheet has
 * to render, but must not be selectable — it records that somebody once ticked a box, which is a
 * weaker claim than any decision made here.
 */
const REASON_LABELS: Record<string, string> = {
  balance_cleared: 'Balance cleared',
  hardship_plan_agreed: 'Hardship — plan agreed',
  default_disputed_resolved: 'Disputed unpaid booking, resolved for the customer',
  system_error: 'System error',
  migrated_from_sheet: 'Carried over from the old spreadsheet — not re-reviewed',
}

const money = (amount: number | string) =>
  Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000))

export function PreviousDefaultWaiver({ bookingId, active }: { bookingId: string; active: boolean }) {
  const [priorDefaults, setPriorDefaults] = useState<PriorDefault[]>([])
  const [unreadable, setUnreadable] = useState<string[]>([])
  // Read only here. Changing them is the settings tab's job; this tab just must not let an
  // analyst work a booking believing the system is in a state it is not.
  const [waiversLive, setWaiversLive] = useState<boolean | null>(null)
  const [autoRejectLive, setAutoRejectLive] = useState<boolean | null>(null)
  const [approvalThreshold, setApprovalThreshold] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loaded, setLoaded] = useState(false)

  const [openFor, setOpenFor] = useState<string | null>(null)
  const [reasonCode, setReasonCode] = useState(REASON_CODES[0])
  const [reasonNote, setReasonNote] = useState('')
  const [requireZeroBalance, setRequireZeroBalance] = useState(true)
  const [approvedBy, setApprovedBy] = useState('')

  const [revokingFor, setRevokingFor] = useState<string | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const [busy, setBusy] = useState(false)

  const closeForms = () => {
    setOpenFor(null)
    setRevokingFor(null)
    setReasonNote('')
    setApprovedBy('')
    setRevokeReason('')
    setRequireZeroBalance(true)
    setReasonCode(REASON_CODES[0])
  }

  // Guards against a slow earlier response landing after a newer one and leaving the wrong
  // customer's defaults on screen.
  const requestId = useRef(0)

  const loadStatus = async () => {
    const status = await getFraudSettings().catch(() => null)
    setWaiversLive(status?.enabled ?? null)
    setAutoRejectLive(status?.autoRejectEnabled ?? null)
  }

  // This panel stays mounted while the settings tab is open, so coming back is the moment the
  // switches may have changed under it. Re-read then, or the warning describes the old state.
  useEffect(() => {
    if (active) loadStatus()
  }, [active])

  const load = async (reference: string) => {
    const thisRequest = ++requestId.current
    setLoading(true)
    setError('')
    try {
      const data = await callApi<{
        priorDefaults: PriorDefault[]
        unreadable: string[]
        approvalThreshold: number
      }>(`/bookings/${reference}/prior-defaults`, {}, 'Failed to load related unpaid bookings')
      if (thisRequest !== requestId.current) return
      setPriorDefaults(data.priorDefaults)
      setUnreadable(data.unreadable ?? [])
      setApprovalThreshold(data.approvalThreshold ?? 0)
      setLoaded(true)
    } catch (e) {
      if (thisRequest !== requestId.current) return
      setError(e instanceof Error ? e.message : 'Failed to load related unpaid bookings')
      // `loaded` goes back to false, not just the list to empty: leaving it true renders the
      // green "no previous defaults" panel over a failed request, which on a fraud screen is an
      // affirmative claim the data does not support.
      setPriorDefaults([])
      setUnreadable([])
      setLoaded(false)
    } finally {
      if (thisRequest === requestId.current) setLoading(false)
    }
  }

  // Same debounce as the fulfilments lookup above it, so typing a reference does not fire a
  // request per keystroke. The list is cleared on the way in, not only when the field empties:
  // otherwise the previous customer's defaults stay on screen through the debounce with live
  // Waive buttons wired to their references.
  useEffect(() => {
    requestId.current++
    setPriorDefaults([])
    setUnreadable([])
    setLoaded(false)
    setError('')
    // The invalidated request's `finally` declines to touch this, so clearing the field mid-flight
    // would otherwise leave `loading` true for a panel that is no longer loading anything.
    setLoading(false)
    closeForms()

    if (!bookingId.trim()) {
      return
    }

    const timer = setTimeout(() => load(bookingId.trim()), 800)
    return () => clearTimeout(timer)
  }, [bookingId])

  const waive = async (reference: string) => {
    setBusy(true)
    setError('')
    try {
      await callApi(
        '/bookings/fraud/default-waiver',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingReference: reference,
            reasonCode,
            reasonNote: reasonNote.trim(),
            requireZeroBalance,
            approvedBy: approvedBy.trim() || undefined,
          }),
        },
        'Failed to record the waiver',
      )
      closeForms()
      await load(bookingId.trim())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record the waiver')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (reference: string) => {
    setBusy(true)
    setError('')
    try {
      await callApi(
        `/bookings/fraud/default-waiver/${reference}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: revokeReason.trim() }),
        },
        'Failed to revoke the waiver',
      )
      closeForms()
      await load(bookingId.trim())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke the waiver')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Related unpaid bookings</h2>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">{error}</div>}

      {/* One line, not two banners: the analyst is here to work a booking, and only needs
          telling when the system is not in its usual state — or when that state is unknown, which
          must not look the same as the usual one. */}
      {(autoRejectLive === null || waiversLive === null) && (
        <div className="mb-3 p-2 bg-gray-100 border border-gray-300 rounded text-xs text-gray-700">
          <strong>Could not read the eligibility settings.</strong> Whether auto-reject and waivers are running is
          unknown right now.
        </div>
      )}

      {(autoRejectLive === false || waiversLive === false) && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
          {autoRejectLive === false && <strong>Auto-reject is paused. </strong>}
          {waiversLive === false && <span>Waivers are recorded but not active. </span>}
          <a
            href={config.routePrefix + '/fraud?tab=settings'}
            className="underline"
          >
            Manage in Eligibility rules
          </a>
        </div>
      )}

      {!bookingId.trim() && (
        <p className="text-sm text-gray-500">
          Enter a booking ID above to see this customer's related unpaid bookings.
        </p>
      )}

      {loading && <div className="p-4 bg-gray-50 rounded-md text-gray-600">Checking previous bookings…</div>}

      {!loading && loaded && unreadable.length > 0 && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
          {unreadable.length} prior booking{unreadable.length === 1 ? '' : 's'} could not be checked:{' '}
          {unreadable.join(', ')}. Treat this list as incomplete.
        </div>
      )}

      {!loading && loaded && priorDefaults.length === 0 && unreadable.length === 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
          No related unpaid bookings for this customer.
        </div>
      )}

      {!loading && priorDefaults.length > 0 && (
        <>
          <p className="mb-3 text-sm text-gray-600">
            Every related unpaid booking in this customer's history. Waiving one skips the automatic cancellation for it
            — the booking still comes here for a manual decision.
          </p>
          <ul className="space-y-3">
            {priorDefaults.map(priorDefault => (
              <li
                key={priorDefault.reference}
                className="border border-gray-200 rounded-md p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span>
                    <span className="font-mono font-semibold text-gray-800">{priorDefault.reference}</span>
                    {/* Found by the inbox alone, under a different name: worth a second look before it
                        is read as the same customer. A same-name booking needs no label. */}
                    {priorDefault.linkedBy.includes('email') && !priorDefault.linkedBy.includes('name') && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-800 border border-amber-200">
                        Same email, different name
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-gray-600">
                    {priorDefault.pastDue === null ? (
                      <span className="text-amber-700">Outstanding: could not be read</span>
                    ) : !priorDefault.balanceVerified ? (
                      <span className="text-amber-700">
                        Outstanding: not verified (Stripe plan or source unknown). Check Stripe.
                      </span>
                    ) : (
                      <>Outstanding: {money(priorDefault.pastDue)}</>
                    )}
                  </span>
                </div>

                {priorDefault.waiver ? (
                  <div className="mt-2">
                    <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2">
                      Waived by {priorDefault.waiver.waivedBy} {daysSince(priorDefault.waiver.created)} days ago —{' '}
                      {REASON_LABELS[priorDefault.waiver.reasonCode] ?? priorDefault.waiver.reasonCode}
                      {priorDefault.waiver.approvedBy ? `, approved by ${priorDefault.waiver.approvedBy}` : ''}.
                      {priorDefault.waiver.requireZeroBalance && !isSettled(priorDefault)
                        ? priorDefault.pastDue === null || !priorDefault.balanceVerified
                          ? ' Effect unknown: the balance could not be verified, so bookings go to manual review.'
                          : ' Not yet in effect: the balance is still outstanding.'
                        : ''}
                      {priorDefault.waiver.reasonNote ? ` Note: ${priorDefault.waiver.reasonNote}` : ''}
                    </p>

                    {revokingFor === priorDefault.reference ? (
                      <div className="mt-2 flex flex-wrap gap-2 items-center">
                        <input
                          id={`revoke-reason-${priorDefault.reference}`}
                          type="text"
                          value={revokeReason}
                          onInput={e => setRevokeReason((e.target as HTMLInputElement).value)}
                          placeholder="Why is this being revoked?"
                          className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <button
                          type="button"
                          disabled={busy || !revokeReason.trim()}
                          onClick={() => revoke(priorDefault.reference)}
                          className="px-3 py-2 bg-red-600 text-white rounded-md disabled:bg-gray-300"
                        >
                          Revoke
                        </button>
                        <button
                          type="button"
                          onClick={closeForms}
                          className="px-3 py-2 text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          closeForms()
                          setRevokingFor(priorDefault.reference)
                        }}
                        className="mt-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Revoke this waiver
                      </button>
                    )}
                  </div>
                ) : openFor === priorDefault.reference ? (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label
                        htmlFor={`reason-code-${priorDefault.reference}`}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Reason
                      </label>
                      <select
                        id={`reason-code-${priorDefault.reference}`}
                        value={reasonCode}
                        onChange={e => setReasonCode((e.target as HTMLSelectElement).value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {REASON_CODES.map(code => (
                          <option
                            key={code}
                            value={code}
                          >
                            {REASON_LABELS[code]}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor={`reason-note-${priorDefault.reference}`}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Note <span className="text-red-500">* why this booking is being waived</span>
                      </label>
                      <input
                        id={`reason-note-${priorDefault.reference}`}
                        type="text"
                        value={reasonNote}
                        onInput={e => setReasonNote((e.target as HTMLInputElement).value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      {/* Required while anything is still owed, optional once it is settled —
                          the same rule the API enforces, said here so it is not a surprise. */}
                      <label
                        htmlFor={`approved-by-${priorDefault.reference}`}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Approved by{' '}
                        {needsSecondName(priorDefault, approvalThreshold) ? (
                          <span className="text-red-500">
                            * second name required
                            {priorDefault.balanceVerified
                              ? ' while a balance is outstanding'
                              : ' because the balance is not verified'}
                          </span>
                        ) : (
                          <span className="text-gray-500">(optional — this balance is settled)</span>
                        )}
                      </label>
                      <input
                        id={`approved-by-${priorDefault.reference}`}
                        type="text"
                        value={approvedBy}
                        onInput={e => setApprovedBy((e.target as HTMLInputElement).value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <label
                      htmlFor={`require-zero-${priorDefault.reference}`}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <input
                        id={`require-zero-${priorDefault.reference}`}
                        type="checkbox"
                        checked={requireZeroBalance}
                        onChange={e => setRequireZeroBalance((e.target as HTMLInputElement).checked)}
                        className="mt-1"
                      />
                      <span>
                        Only take effect once this balance is cleared. Leave this on unless the customer has already
                        paid — a waiver granted on a promise otherwise lets them book again while still owing.
                      </span>
                    </label>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={
                          busy ||
                          !reasonNote.trim() ||
                          priorDefault.pastDue === null ||
                          (needsSecondName(priorDefault, approvalThreshold) && !approvedBy.trim())
                        }
                        onClick={() => waive(priorDefault.reference)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-300"
                      >
                        {busy ? 'Saving…' : 'Waive this booking'}
                      </button>
                      <button
                        type="button"
                        onClick={closeForms}
                        className="px-4 py-2 text-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={priorDefault.pastDue === null}
                    title={
                      priorDefault.pastDue === null
                        ? 'The balance on this booking could not be read, so it cannot be waived yet'
                        : undefined
                    }
                    onClick={() => {
                      closeForms()
                      setOpenFor(priorDefault.reference)
                    }}
                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 disabled:text-gray-400"
                  >
                    Waive this booking
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
